import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import LiveModeBadge from "@/components/LiveModeBadge";
import DateRangePicker, { rangeForPreset } from "@/components/DateRangePicker";
import { useLocale } from "@/contexts/LocaleContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  CalendarPlus, Plus, ChevronLeft, ChevronRight, Mail, Send, MapPin, Clock3,
  RefreshCw, X, ListChecks, CalendarRange, Calendar as CalendarIcon, AlertTriangle,
} from "lucide-react";

// PR-2C close-out follow-up: the list view's API window is now driven by a
// DateRangePicker (presets + custom). The 200-row server cap surfaces a
// "narrow your range" notice. Calendar view keeps its own week navigation —
// the picker only constrains the LIST query.
const LIST_LIMIT = 200;

// ---- helpers ----
function startOfWeek(d = new Date()) {
  const dt = new Date(d);
  const day = dt.getDay() === 0 ? 6 : dt.getDay() - 1; // Mon-first
  dt.setDate(dt.getDate() - day);
  dt.setHours(0, 0, 0, 0);
  return dt;
}
function addDays(d, n) {
  const dt = new Date(d);
  dt.setDate(dt.getDate() + n);
  return dt;
}
function fmtDay(d) {
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
}
function fmtTime(iso) {
  // Phase B2 — kept as a legacy fallback. Inside the component we use
  // useLocale().formatTime() which honours per-user/org locale.
  try { return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }); }
  catch { return iso || ""; }
}
function isoToDate(iso) {
  try { return new Date(iso); } catch { return null; }
}

const DOW_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function PreRegistrations() {
  const { formatDate, formatTime } = useLocale();
  const [view, setView] = useState("calendar"); // calendar | list
  const [weekStart, setWeekStart] = useState(startOfWeek());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerId, setDrawerId] = useState(null);
  const [dragState, setDragState] = useState({ active: false, item: null });

  // PR-2C close-out follow-up — list-view date range. The default keeps the
  // pre-existing "this week" behaviour so we don't surprise users on first
  // load. The custom range powers the 2099 simulate-event visibility test.
  const [range, setRange] = useState(() => {
    const r = rangeForPreset("this_week");
    return { preset: "this_week", from: r.from, to: r.to };
  });

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  // The list query uses the picker range; the calendar view keeps its
  // dedicated week window. Two distinct fetches keep concerns clean.
  const load = async () => {
    setLoading(true);
    try {
      if (view === "list") {
        const { data } = await api.get("/pre-registrations", {
          params: {
            from: range.from.toISOString(),
            to: range.to.toISOString(),
            limit: LIST_LIMIT,
          },
        });
        setItems(data || []);
      } else {
        const from = weekStart.toISOString();
        const to = addDays(weekStart, 7).toISOString();
        const { data } = await api.get("/pre-registrations", { params: { from, to } });
        setItems(data || []);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [weekStart, view, range.from, range.to]);

  const itemsByDay = useMemo(() => {
    const map = {};
    days.forEach((d) => { map[d.toDateString()] = []; });
    items.forEach((it) => {
      const dt = isoToDate(it.expected_at);
      if (!dt) return;
      const key = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()).toDateString();
      if (map[key]) map[key].push(it);
    });
    Object.values(map).forEach((arr) => arr.sort((a, b) =>
      new Date(a.expected_at).getTime() - new Date(b.expected_at).getTime()
    ));
    return map;
  }, [items, days]);

  const onDropToDay = async (targetDay) => {
    if (!dragState.item) return;
    const old = isoToDate(dragState.item.expected_at);
    if (!old) return;
    const newDt = new Date(targetDay);
    newDt.setHours(old.getHours(), old.getMinutes(), 0, 0);
    try {
      await api.patch(`/pre-registrations/${dragState.item.id}`, { expected_at: newDt.toISOString() });
      toast.success("Rescheduled");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setDragState({ active: false, item: null });
  };

  const resend = async (id) => {
    try {
      await api.post(`/pre-registrations/${id}/resend-invitation`);
      toast.success("Invitation resent (mocked while in dev)");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AppShell>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="eb-overline mb-2">Module 5 · Pre-registrations</div>
          <h1 className="text-3xl font-semibold flex items-center gap-3" data-testid="pre-regs-heading">
            Pre-registrations
            <LiveModeBadge service="resend" />
          </h1>
          <p className="text-sm text-navy-500 mt-1">Plan visitors, send QR-coded invitations, manage recurring visits.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* PR-2C close-out follow-up — picker only relevant for the LIST view;
              the calendar view has its own week navigator. We still render it
              for both so the chrome doesn't shift between modes. */}
          <DateRangePicker value={range} onChange={setRange} />
          <div className="bg-white border border-navy-100 rounded-lg p-1 flex" data-testid="pre-regs-view-toggle">
            <button
              onClick={() => setView("calendar")}
              className={`px-3 py-1.5 rounded-md text-xs font-medium inline-flex items-center gap-1.5 ${view === "calendar" ? "bg-navy-900 text-white" : "text-navy-700"}`}
              data-testid="pre-regs-view-calendar"
            ><CalendarRange className="w-3.5 h-3.5" /> Calendar</button>
            <button
              onClick={() => setView("list")}
              className={`px-3 py-1.5 rounded-md text-xs font-medium inline-flex items-center gap-1.5 ${view === "list" ? "bg-navy-900 text-white" : "text-navy-700"}`}
              data-testid="pre-regs-view-list"
            ><ListChecks className="w-3.5 h-3.5" /> List</button>
          </div>
          <Link to="/pre-registrations/new" className="btn-accent" data-testid="pre-regs-new-btn">
            <Plus className="w-4 h-4" /> New
          </Link>
        </div>
      </div>

      {view === "calendar" && (
        <div className="eb-card p-0 overflow-hidden" data-testid="pre-regs-calendar">
          <div className="flex items-center justify-between p-4 border-b border-navy-100">
            <div className="flex items-center gap-2">
              <button onClick={() => setWeekStart(addDays(weekStart, -7))} className="btn-ghost" data-testid="cal-prev-week">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button onClick={() => setWeekStart(startOfWeek())} className="btn-outline text-xs" data-testid="cal-this-week">
                This week
              </button>
              <button onClick={() => setWeekStart(addDays(weekStart, 7))} className="btn-ghost" data-testid="cal-next-week">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <div className="text-sm text-navy-700 font-medium" data-testid="cal-range">
              {weekStart.toLocaleDateString("en-GB", { day: "numeric", month: "short" })} – {addDays(weekStart, 6).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
            </div>
            <button onClick={load} className="btn-ghost text-xs" data-testid="cal-refresh">
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="grid grid-cols-7 min-h-[420px]">
            {days.map((d, i) => {
              const dayItems = itemsByDay[d.toDateString()] || [];
              const isToday = d.toDateString() === new Date().toDateString();
              return (
                <div
                  key={i}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => onDropToDay(d)}
                  className={`border-r border-navy-100 last:border-r-0 ${isToday ? "bg-orange-50/40" : "bg-white"}`}
                  data-testid={`cal-day-${i}`}
                >
                  <div className={`px-3 py-2 border-b border-navy-100 text-xs font-semibold uppercase tracking-wider ${isToday ? "text-orange-600" : "text-navy-500"}`}>
                    {DOW_LABELS[i]} <span className="ml-1 text-navy-700">{d.getDate()}</span>
                  </div>
                  <div className="p-2 space-y-2">
                    {dayItems.length === 0 ? (
                      <div className="text-[11px] text-navy-300 px-1 py-3">—</div>
                    ) : dayItems.map((it) => (
                      <div
                        key={it.id}
                        draggable
                        onDragStart={() => setDragState({ active: true, item: it })}
                        onClick={() => setDrawerId(it.id)}
                        className={`rounded-md border bg-white px-2 py-2 text-xs cursor-pointer hover:shadow-sm transition-shadow ${
                          it.invitation_status === "used" ? "border-success/40 bg-success/5"
                            : it.invitation_status === "cancelled" ? "border-navy-200 opacity-60 line-through"
                            : "border-orange-200"
                        }`}
                        data-testid={`cal-item-${it.id}`}
                      >
                        <div className="font-semibold text-navy-900 truncate inline-flex items-center gap-1">
                          {it.visitor_name}
                          <SourceBadge source={it.source} sourceLabel={it.source_label} compact />
                        </div>
                        <div className="text-navy-500 flex items-center gap-1 mt-0.5">
                          <Clock3 className="w-3 h-3" /> {formatTime(it.expected_at)}
                        </div>
                        {it.company && <div className="text-navy-400 truncate">{it.company}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {view === "list" && (
        <div className="eb-card p-0 overflow-hidden" data-testid="pre-regs-list">
          {/* Range header — shows the active window in British format. */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-navy-100 bg-navy-50/40">
            <div
              className="text-xs text-navy-600"
              data-testid="pre-regs-list-range-summary"
            >
              Showing {items.length} {items.length === 1 ? "entry" : "entries"} from{" "}
              <strong className="text-navy-900">{formatDate(range.from)}</strong> to{" "}
              <strong className="text-navy-900">{formatDate(range.to)}</strong>
            </div>
            <button onClick={load} className="btn-ghost text-xs" data-testid="list-refresh">
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>
          {/* Server caps at 200 — surface a clear "narrow your range" hint
              when we hit the cap. PR-2C close-out follow-up. */}
          {items.length >= LIST_LIMIT && (
            <div
              className="px-4 py-2 bg-orange-50 border-b border-orange-100 text-xs text-orange-700 flex items-center gap-2"
              data-testid="pre-regs-list-cap-notice"
            >
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
              Showing the first {LIST_LIMIT} entries — narrow your range to see more.
            </div>
          )}
          {loading ? (
            <p className="text-navy-500 py-10 text-center">Loading…</p>
          ) : items.length === 0 ? (
            <div className="p-10 text-center">
              <CalendarPlus className="w-10 h-10 text-navy-300 mx-auto mb-3" />
              <p className="text-sm text-navy-500" data-testid="pre-regs-list-empty">
                No pre-registrations in this range.
              </p>
              <Link to="/pre-registrations/new" className="btn-accent mt-4" data-testid="list-empty-cta">
                <Plus className="w-4 h-4" /> New pre-registration
              </Link>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-navy-50/60 text-navy-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">When</th>
                  <th className="text-left px-4 py-3">Visitor</th>
                  <th className="text-left px-4 py-3">Company</th>
                  <th className="text-left px-4 py-3">Type</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-t border-navy-100 hover:bg-navy-50/40 cursor-pointer" onClick={() => setDrawerId(it.id)} data-testid={`list-row-${it.id}`}>
                    <td className="px-4 py-3" data-testid={`list-when-${it.id}`}>
                      {/* Phase B2 — locale-aware date + time. `formatDate`
                          renders DD/MM/YYYY for en-GB / en-IE and MM/DD/YYYY
                          for en-US; `formatTime` renders 24h or 12h to match. */}
                      <span className="font-medium">{formatDate(it.expected_at)}</span>
                      <span className="text-navy-400 ml-2">{formatTime(it.expected_at)}</span>
                    </td>
                    <td className="px-4 py-3 font-medium">
                      <span className="inline-flex items-center gap-1.5">
                        {it.visitor_name}
                        <SourceBadge source={it.source} sourceLabel={it.source_label} />
                      </span>
                    </td>
                    <td className="px-4 py-3">{it.company || "—"}</td>
                    <td className="px-4 py-3">{it.visitor_type}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full ${
                        it.invitation_status === "used" ? "bg-success/15 text-success"
                          : it.invitation_status === "cancelled" ? "bg-navy-100 text-navy-500"
                          : "bg-orange-100 text-orange-700"
                      }`}>{it.invitation_status}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={(e) => { e.stopPropagation(); resend(it.id); }} className="btn-ghost text-xs" data-testid={`list-resend-${it.id}`}>
                        <Send className="w-3.5 h-3.5" /> Resend
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {drawerId && <PreRegDrawer id={drawerId} onClose={() => setDrawerId(null)} onChanged={load} />}
    </AppShell>
  );
}

function PreRegDrawer({ id, onClose, onChanged }) {
  const [data, setData] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/pre-registrations/${id}`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); onClose(); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const cancel = async () => {
    if (!window.confirm("Cancel this pre-registration?")) return;
    try {
      await api.delete(`/pre-registrations/${id}`);
      toast.success("Cancelled");
      onChanged();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resend = async () => {
    try {
      await api.post(`/pre-registrations/${id}/resend-invitation`);
      toast.success("Invitation resent");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!data) return null;
  return (
    <div className="fixed inset-0 z-50 bg-navy-900/40 backdrop-blur-sm flex justify-end" onClick={onClose} data-testid="pre-reg-drawer">
      <div className="w-full max-w-md bg-white h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-navy-100 p-5 flex items-center justify-between">
          <div>
            <div className="eb-overline">Pre-registration</div>
            <div className="text-lg font-semibold">{data.visitor_name}</div>
          </div>
          <button onClick={onClose} className="btn-ghost"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <Row label="When">{new Date(data.expected_at).toLocaleString("en-GB")}</Row>
          <Row label="Email">{data.visitor_email || "—"}</Row>
          <Row label="Phone">{data.visitor_phone || "—"}</Row>
          <Row label="Company">{data.company || "—"}</Row>
          <Row label="Type">{data.visitor_type}</Row>
          <Row label="Purpose">{data.purpose || "—"}</Row>
          <Row label="Site">{data.site_id?.slice(0, 8) || "—"}</Row>
          <Row label="Status">
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
              data.invitation_status === "used" ? "bg-success/15 text-success"
                : data.invitation_status === "cancelled" ? "bg-navy-100 text-navy-500"
                : "bg-orange-100 text-orange-700"
            }`}>{data.invitation_status}</span>
          </Row>
          <Row label="Send attempts">{data.invitation_send_attempts ?? 0}</Row>
        </div>
        <div className="p-5 flex gap-2 border-t border-navy-100">
          {data.invitation_status !== "cancelled" && data.invitation_status !== "used" && (
            <>
              <button onClick={resend} className="btn-outline" data-testid="drawer-resend"><Send className="w-4 h-4" /> Resend</button>
              <button onClick={cancel} className="btn-ghost text-danger" data-testid="drawer-cancel"><X className="w-4 h-4" /> Cancel</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-3 gap-3">
      <div className="text-navy-400 uppercase tracking-wider text-[10px] font-semibold">{label}</div>
      <div className="col-span-2">{children}</div>
    </div>
  );
}


// v2 PR-2C — small icon badge for non-manual pre-reg sources.
function SourceBadge({ source, sourceLabel, compact }) {
  if (!source || source === "manual") return null;
  const map = {
    "calendar.google": { Icon: CalendarIcon, label: sourceLabel || "Synced from Google Calendar" },
    "calendar.microsoft": { Icon: CalendarRange, label: sourceLabel || "Synced from Microsoft 365" },
    "api": { Icon: RefreshCw, label: sourceLabel || "Created via Public API" },
  };
  const cfg = map[source];
  if (!cfg) return null;
  const Icon = cfg.Icon;
  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded-full bg-orange-50 text-orange-600 border border-orange-200 ${compact ? "px-1 py-0" : "px-1.5 py-0.5"}`}
      title={cfg.label}
      data-testid={`source-badge-${source.replace(/\./g, "-")}`}
    >
      <Icon className={compact ? "w-2.5 h-2.5" : "w-3 h-3"} />
    </span>
  );
}
